import type { ChangeEvent, FC, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import InputCheckbox from '@components/InputCheckbox';
import InputMultiSelect from '@components/InputMultiSelect';
import type { PopoverProps } from '@components/Popover';
import { Popover } from '@components/Popover';
import { Show } from '@components/Show';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import { useAssignableTeams } from '@queries/fetchTeams';
import { ALL_CLASSES_OF_BUSINESS } from '@root/helpers/constants';
import { useLanguage } from '@src/language';
import type { ColumnFilter, Table } from '@tanstack/react-table';
import { BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING } from '@User/Blocks/constants';

import { FiltersTrigger } from './FiltersTrigger';

interface SearchFiltersDropdownProps {
  filters: ColumnFilter[];
  table: Table<LibraryBlockTypeForListPage>;
  renderTrigger: PopoverProps['renderTrigger'];
  isFetching: boolean;
  isClause: boolean;
}

type ResolveName = (raw: string) => string;

const FilterTitle = ({ children }: { children: ReactNode }) => (
  <div className="text-info-500 mt-3 mb-0 leading-[18px] font-semibold uppercase first:mt-1">{children}</div>
);

// Filter menus portal to <body>, so they fall outside the click-away ref; the handler matches this
// marker class to keep the panel open on menu clicks. Both InputMultiSelects below must set it.
const FILTER_MENU_CLASS = 'block-library-filter-menu';

export const SearchFiltersDropdown: FC<SearchFiltersDropdownProps> = ({
  filters: currentFiltersState,
  renderTrigger,
  isClause,
  table,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { getContent } = useLanguage({ prefix: 'naming.mrc.block.layout' });
  const { data: teams = [] } = useAssignableTeams();

  const blockTypeLabel = (value: string) =>
    BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.find((i) => i.value === value)?.label ?? '';

  const selectedFilterValues = (columnId: string) =>
    (currentFiltersState?.find(({ id }) => id === columnId)?.value as string[]) ?? [];

  const makeCheckboxHandler =
    (columnId: string, resolveName?: ResolveName) => (event: ChangeEvent<HTMLInputElement>) => {
      const name = resolveName?.(event.target.name) ?? event.target.name;
      const current = selectedFilterValues(columnId);

      table
        .getColumn(columnId)
        ?.setFilterValue(event.target.checked ? [...current, name] : current.filter((i) => i !== name));
    };

  const makeCheckboxProps =
    (
      columnId: string,
      onChange: (event: ChangeEvent<HTMLInputElement>) => void,
      resolveChecked: (v: string) => string = (v) => v,
    ) =>
    (value: string) => ({
      isChecked: selectedFilterValues(columnId).includes(resolveChecked(value)),
      name: value,
      onChange,
    });

  const handleBlockTypeChange = makeCheckboxHandler('type', blockTypeLabel);
  const handleDropdownFilterChange = (key: string) => (selectedValue?: string[] | number[]) => {
    table.getColumn(key)?.setFilterValue(selectedValue);
  };

  const getBlockTypeInputProps = makeCheckboxProps('type', handleBlockTypeChange, blockTypeLabel);
  const getClauseTypeInputProps = makeCheckboxProps('clause_type', makeCheckboxHandler('clause_type'));
  const getClassificationInputProps = makeCheckboxProps('classification', makeCheckboxHandler('classification'));

  const handleOpenFilters = () => setIsFiltersOpen(true);

  const popoverRef = useRef<HTMLDivElement>(null);

  useClickAway(popoverRef, (event) => {
    const target = event.target as Element;
    if (!target.closest('[data-radix-popper-content-wrapper]') && !target.closest(`.${FILTER_MENU_CLASS}`)) {
      setIsFiltersOpen(false);
    }
  });

  return (
    <>
      <FiltersTrigger isFiltersOpen={isFiltersOpen} openFilters={handleOpenFilters} />
      <Show when={isFiltersOpen}>
        <Popover
          forceIsOpen
          containerClassName="relative flex flex-col bg-white grow w-[270px] rounded-lg"
          isMinimal
          middleware={[]}
          offset={2}
          renderTrigger={renderTrigger}
          wrapperClassName="ring-1 ring-black/5 bg-white shadow-lg w-[270px] flex rounded-lg"
        >
          {() => (
            <div ref={popoverRef} className="flex flex-col gap-1.5 rounded-lg rounded-b-none bg-white p-4 text-sm">
              <Show when={!isClause}>
                <FilterTitle>Block layout</FilterTitle>
                <InputCheckbox
                  {...getBlockTypeInputProps('generic')}
                  dataTestId="checkbox:generic"
                  labelText={getContent('generic')}
                />
                <InputCheckbox
                  {...getBlockTypeInputProps('mrc-heading')}
                  id="generic"
                  dataTestId="checkbox:mrc-heading"
                  labelText={getContent('mrc-heading')}
                />
              </Show>
              <Show when={isClause}>
                <FilterTitle>Clause type</FilterTitle>
                <InputCheckbox
                  {...getClauseTypeInputProps('Condition')}
                  labelText="Condition"
                  dataTestId="checkbox:condition"
                />
                <InputCheckbox
                  {...getClauseTypeInputProps('Endorsement')}
                  labelText="Endorsement"
                  dataTestId="checkbox:endorsement"
                />
                <InputCheckbox
                  {...getClauseTypeInputProps('Exclusion')}
                  labelText="Exclusion"
                  dataTestId="checkbox:exclusion"
                />
                <InputCheckbox
                  {...getClauseTypeInputProps('Standard Warranties')}
                  labelText="Standard Warranties"
                  dataTestId="checkbox:standard-warranties"
                />
                <InputCheckbox
                  {...getClauseTypeInputProps('Express Warranties')}
                  labelText="Express Warranties"
                  dataTestId="checkbox:express-warranties"
                />
                <InputCheckbox
                  {...getClauseTypeInputProps('Wording')}
                  labelText="Wording"
                  dataTestId="checkbox:wording"
                />
                <FilterTitle>Clause classification</FilterTitle>
                <InputCheckbox
                  {...getClassificationInputProps('Standard')}
                  labelText="Standard"
                  dataTestId="checkbox:standard"
                />
                <InputCheckbox
                  {...getClassificationInputProps('Non-Standard')}
                  labelText="Non-Standard"
                  dataTestId="checkbox:non-standard"
                />
                <InputCheckbox {...getClassificationInputProps('LMW')} labelText="LMW" dataTestId="checkbox:lmw" />
              </Show>
              <FilterTitle>Class of business</FilterTitle>
              <InputMultiSelect
                onChange={handleDropdownFilterChange('class_of_business')}
                options={ALL_CLASSES_OF_BUSINESS}
                value={table.getColumn('class_of_business')?.getFilterValue() as string[]}
                dataTestId="multiselect:search-class-of-business"
                shouldRenderInsidePortal
                menuOuterClassName={FILTER_MENU_CLASS}
              />
              <FilterTitle>Team</FilterTitle>
              <InputMultiSelect<number>
                onChange={handleDropdownFilterChange('teams')}
                options={teams.map((team) => ({ name: team.name, value: team.id }))}
                value={table.getColumn('teams')?.getFilterValue() as number[]}
                dataTestId="multiselect:search-teams"
                shouldRenderInsidePortal
                menuOuterClassName={FILTER_MENU_CLASS}
              />
            </div>
          )}
        </Popover>
      </Show>
    </>
  );
};
